import Entity from "../../core/entity/Entity";
import { choose } from "../../core/util/Random";
import { V, V2d } from "../../core/Vector";
import { LevelEntity } from "../LevelEntity";
import { InterceptorEnemy } from "./InterceptorEnemy";

const INTERCEPTORS_PER_SWARM = 12;
const SPAWN_DELAY = 0.1; // Seconds between spawning each interceptor
const SPLIT_DISTANCE = 150; // Distance from center when interceptors split

enum PathType {
  SINE = "sine",
  SPIRAL = "spiral",
  FIGURE8 = "figure8",
  ZIGZAG = "zigzag",
  HELIX = "helix",
}

export class InterceptorSwarm extends LevelEntity implements Entity {
  tags = ["interceptorSwarm"];
  private spawnPosition: V2d;
  private interceptors: InterceptorEnemy[] = [];
  private waypoints: V2d[] = [];
  private pathType: PathType;

  constructor(spawnPosition: V2d) {
    super();
    this.spawnPosition = spawnPosition.clone();
    // Randomly select path type for variety
    this.pathType = choose(
      PathType.SINE,
      PathType.SPIRAL,
      PathType.FIGURE8,
      PathType.ZIGZAG,
      PathType.HELIX,
    );
  }

  async onAdd(): Promise<void> {
    // Generate waypoints for the swarm's approach path
    this.generateWaypoints();

    // Spawn all interceptors with delay
    for (let i = 0; i < INTERCEPTORS_PER_SWARM; i++) {
      this.spawnInterceptor();
      if (i < INTERCEPTORS_PER_SWARM - 1) {
        await this.wait(SPAWN_DELAY);
      }
    }

    // Monitor for all interceptors being destroyed
    await this.waitUntil(() => this.interceptors.every((i) => i.isDestroyed()));
    this.destroy();
  }

  private generateWaypoints(): void {
    // Sample many points along the path for smooth movement
    const numWaypoints = 50;

    for (let i = 0; i <= numWaypoints; i++) {
      const t = i / numWaypoints; // 0 to 1
      const waypoint = this.getPathPoint(t);
      this.waypoints.push(waypoint);
    }
  }

  private getPathPoint(t: number): V2d {
    const toCenter = V(0, 0).sub(this.spawnPosition);
    const distance = toCenter.magnitude;
    const direction = toCenter.normalize();
    const perpendicular = direction.rotate90cw();
    const totalDistance = distance - SPLIT_DISTANCE;
    const forwardProgress = totalDistance * t;
    const basePoint = this.spawnPosition.add(direction.mul(forwardProgress));

    switch (this.pathType) {
      case PathType.SINE:
        // Original smooth S-curves
        const lateralOffset = Math.sin(t * Math.PI * 2) * 60;
        return basePoint.add(perpendicular.mul(lateralOffset));

      case PathType.SPIRAL:
        // Spiral inward
        const radius = totalDistance * (1 - t); // Decreasing radius
        const angle = t * Math.PI * 4; // 2 full rotations
        const spiralOffset = V(
          Math.cos(angle) * radius * 0.3,
          Math.sin(angle) * radius * 0.3,
        );
        return basePoint.add(spiralOffset);

      case PathType.FIGURE8:
        // Figure-8 using Lissajous curve
        const lateralFig8 = Math.sin(t * Math.PI * 2) * 60;
        const verticalFig8 = Math.sin(t * Math.PI * 4) * 30;
        return basePoint
          .add(perpendicular.mul(lateralFig8))
          .add(direction.rotate90cw().mul(verticalFig8));

      case PathType.ZIGZAG:
        // Sharp zigzag
        const zigzagPhase = Math.floor(t * 6); // 6 zigs
        const zigzagDirection = zigzagPhase % 2 === 0 ? 1 : -1;
        const zigzagOffset = zigzagDirection * 80;
        return basePoint.add(perpendicular.mul(zigzagOffset));

      case PathType.HELIX:
        // 3D helix projected to 2D
        const helixAngle = t * Math.PI * 6; // 3 full rotations
        const helixRadius = 40 * (1 - t * 0.5); // Tightening helix
        const helixOffset = V(
          Math.cos(helixAngle) * helixRadius,
          Math.sin(helixAngle) * helixRadius,
        );
        return basePoint.add(helixOffset);

      default:
        return basePoint;
    }
  }

  private spawnInterceptor(): void {
    if (!this.game) return;

    const interceptor = new InterceptorEnemy(
      this.spawnPosition,
      this.waypoints,
    );
    this.interceptors.push(interceptor);

    // Add to level
    this.getCurrentLevel()?.addChild(interceptor);
  }
}
